import { GenericModelApi } from "./GenericModelApi";
import { ApiClient, ApiError, ApiSchema, FetchResponse } from "./ApiClient";
import { ClientCreditFormData } from "@/formTypes/ClientCreditFormDataType";
import { CLIENT_CREDIT_ACTIVE_STATUS } from "@utils/Constants";
import { generateNextNumberByModel } from "@utils/Utils";
import { DocumentAutoGeneratedNumberApi } from "./DocumentAutoGeneratedNumberApi";

export type ClientCreditDataType = Partial<ApiSchema["ClientCredit"]["type"] & ClientCreditFormData>;

class Api extends GenericModelApi<"ClientCredit", ClientCreditDataType> {

    /**
     * Crea el saldo a favor asignandole el consecutivo del modelo, igual que
     * ProviderOrderApi/RecibeApi.
     */
    async createClientCredit(input: ClientCreditDataType): Promise<ClientCreditDataType> {
        const generatedNumber = await generateNextNumberByModel("ClientCredit");
        if (!generatedNumber?.number) {
            throw Error(`No fue posible generar el identificador del saldo a favor`)
        }

        const created = await this.create({
            ...input,
            numero: generatedNumber.number
        });

        await DocumentAutoGeneratedNumberApi.save(generatedNumber)

        return created;
    }

    /**
     * Saldos a favor de un cliente, del mas reciente al mas antiguo.
     * El modelo no declara relaciones, asi que no hay selectionSet anidado:
     * el nombre del cliente y el numero de recibo viajan denormalizados.
     */
    async fetchAllByClient(clientId: string): Promise<FetchResponse<ClientCreditDataType>> {
        const fetchFunction = ApiClient.models[this.modelName].listClientCreditByClientId;

        const { data, errors } = await fetchFunction({
            clientId
        }, {
            limit: 1000,
            sortDirection: "DESC"
        });

        if (errors) {
            throw new ApiError("Error al obtener los saldos a favor del cliente", "API_ERROR", errors);
        }

        return { items: data };
    }

    /**
     * Saldos a favor con monto disponible, para el estado de cuenta.
     */
    async fetchAvailableByClient(clientId: string, endDate?: string): Promise<FetchResponse<ClientCreditDataType>> {
        const { items } = await this.fetchAllByClient(clientId);

        const availableItems = items.filter(item => {
            if (item.estado !== CLIENT_CREDIT_ACTIVE_STATUS) {
                return false;
            }
            if (Number(item.monto || 0) - Number(item.aplicado || 0) <= 0) {
                return false;
            }
            return endDate ? `${item.created}` <= endDate : true;
        });

        return { items: availableItems };
    }
}

export const ClientCreditApi = new Api("ClientCredit", "descripcion")
